import { Component, OnInit } from '@angular/core';
import { RouterOutlet, NavigationEnd, Router } from '@angular/router';

import { HeaderComponent } from './header/header.component';
import { SidebarComponent } from "./layout/sidebar/sidebar.component";
import { TokenService } from './services/token.service';
import { TranslateService } from '@ngx-translate/core';
import { UsersService } from './services/users.service';
import { firstValueFrom, filter } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { SwUpdate } from '@angular/service-worker';
import { TokenCheckService } from './services/token-check.service';
import { ToastComponent } from './_utils/components/toast/toast.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, SidebarComponent, ToastComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  title = 'staffing';

  constructor(
    private tokenService: TokenService,
    private translate: TranslateService,
    private usersService: UsersService,
    private router: Router,
    private swUpdate: SwUpdate,
    private tokenCheckService: TokenCheckService
  ) {
    if (swUpdate.isEnabled) {
      swUpdate.versionUpdates.subscribe(async evt => {
        if (evt.type === 'VERSION_READY') {
          const message = await firstValueFrom(
            this.translate.get('APP.NEW_VERSION_AVAILABLE')
          );
          if (confirm(message)) {
            window.location.reload();
          }
        }
      });
      // force check for updates at startup
      this.swUpdate.checkForUpdate();

      //Set periodic check for updates (every 15 minutes)
      setInterval(() => {
        console.log('Periodic check for updates...');
        this.swUpdate.checkForUpdate();
      }, 15 * 60 * 1000); // in milliseconds
    }
  }

  ngOnInit(): void {
    //launch token expiration check loop
    this.tokenCheckService.startCheckInterval();

    // Manage user language preferences
    this.manageUSerLanguage();
  }
  
  

  /**
   * Manages the user's language for the entire application.
   *
   * 1. When the application starts, loads the language stored in the localStorage.
   * 2. After a user logs in, loads the user's language from the API if available.
   *    If the language is not available, uses the default language.
   *    Stores the user's language in the localStorage.
   */
  private manageUSerLanguage(): void {
    // Étape 1 : Gérer la langue au démarrage de l'application
    const langFromStorage = typeof window !== 'undefined' ? localStorage.getItem('lang') : null;
    if (langFromStorage) {
      this.translate.use(langFromStorage);
      // console.log(`AppComponent : Langue initiale chargée depuis le localStorage : ${langFromStorage}`);
    } else {
      // console.log('AppComponent : Aucune langue trouvée dans le localStorage. La langue par défaut est utilisée.');
    }

    // Étape 2 : Mettre à jour la langue de l'utilisateur après la connexion
    // Nous écoutons la navigation pour vérifier la langue après le login
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      // Vérifier si l'utilisateur est connecté et si nous sommes sur la page d'accueil après un login
      if (this.tokenService.isLogged()) {
        // console.log('AppComponent : Détection d\'une connexion. Tentative de chargement de la langue depuis l\'API...');
        this.loadUserLanguage();
      }
    });
  }

  
  /**
   * Tries to load the user's language from the API when the user is logged in.
   * If the language is found, it sets the language of the application with the TranslateService
   * and stores the language in the localStorage.
   * If the language is not found, it leaves the application's language unchanged.
   * If an error occurs, it logs the error in the console.
   */
  private async loadUserLanguage(): Promise<void> {
    try {
      const userFromApi = await firstValueFrom(this.usersService.getCurrentUser());
      if (userFromApi && userFromApi.languageCode) {
        const langToUse = userFromApi.languageCode;
        this.translate.use(langToUse);
        localStorage.setItem('lang', langToUse);
        window.location.reload();
      } else {
        // console.log('AppComponent : Les données utilisateur ne contiennent pas de code de langue.');
      }
    } catch (err) {
      if (err instanceof HttpErrorResponse) {
        // console.error('AppComponent : Erreur lors du chargement de la langue utilisateur. Le token est peut-être expiré ou invalide. Erreur :', err);
      } else {
        // console.error('AppComponent : Erreur inattendue lors du chargement de la langue', err);
      }
    }
  }

  /**
   * Returns true if a token is present in the localStorage, false otherwise.
   * @returns True if a token is present.
   */
  isLogged(): boolean {
    return this.tokenService.isLogged();
  }
}
